import React from "react";
import { useRouteMatch } from "react-router";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import debounce from "lodash/debounce";
import get from "lodash/get";
import { Input, Modal, Select, Form, Button } from "antd";
import { CheckCircleFilled, EditOutlined } from "@ant-design/icons";
import FromNow from "../../atoms/FromNow";
import { StatusLive } from "../../../models/liveStream";
import styles from "./LiveStreamingChat.module.scss";
import { UpdateCommentInput, Program } from "../../../API";
import LocalStorage, {
  LocalStorageKey,
} from "../../../local-storage/local-storage";
import useComment from "../../../hooks/useComment";
import useCartIn from "../../../hooks/useCartIn";
import { ViewPage } from "../../../common-definitions/routes";
import icSendPc from "../../../assets/img/ic_send_pc.svg";
import icSendPcDark from "../../../assets/img/ic_send_pc_dark.svg";
import icSendSp from "../../../assets/img/ic_send_sp.svg";
import icSendSpDark from "../../../assets/img/ic_send_sp_dark.svg";
import { useViewport } from "../../../hooks/useViewport";
import { isMobileOrTablet } from "../../../common-definitions/device";
import {
  LiveStreamStatus,
  StickyComment,
} from "../../../common-definitions/common-enum";

const { Option } = Select;
const { confirm } = Modal;

interface LiveStreamingChatProps {
  statusLive: number;
  program: Program;
  isOverlay?: boolean;
  fetched: boolean;
}

const LiveStreamingChat: React.FC<LiveStreamingChatProps> = ({
  statusLive,
  program,
  isOverlay = false,
  fetched,
}) => {
  const url = useRouteMatch<ViewPage>();
  const { comments, createComment } = useComment(url.params.id);
  const { updateCartInUserName } = useCartIn(url.params.id);
  const ref = React.useRef<HTMLDivElement>(null);
  const refChatList = React.useRef<HTMLDivElement>(null);
  const refStickyInner = React.useRef<HTMLDivElement>(null);
  const refInputConfirm = React.useRef<Input>(null);
  const refInputComment = React.useRef<HTMLInputElement>(null);
  const [state, setState] = React.useState({
    filter: "all",
    triggerScrollBottom: true,
  });
  const [hideComment, setHideComment] = React.useState(false);
  const [isFetched, setIsFetched] = React.useState(false);
  const [disableComment, setDisableSubmit] = React.useState(true);
  const [userName, setUserName] = React.useState(
    LocalStorage.get(LocalStorageKey.userName) || ""
  );
  const [hasPinExpand, setHasPinExpand] = React.useState(false);
  const [pinExpand, setPinExpand] = React.useState(false);

  const isMobile = useViewport() <= 1024 || isMobileOrTablet();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const timeoutRef = React.useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const delayScroll = React.useRef<any>(null);

  React.useEffect(() => {
    setTimeout(() => {
      setIsFetched(!!fetched);
    }, 300);
  }, [fetched]);

  const commentList = React.useMemo(() => {
    return (
      (state.filter === "manager"
        ? comments?.filter((comment: UpdateCommentInput) => {
            return comment.userId === "manager";
          })
        : comments) || ([] as UpdateCommentInput[])
    );
  }, [state.filter, comments]);

  const pinComment = (commentList || []).filter(
    (cmt: UpdateCommentInput) => cmt.isSticky === StickyComment.IS_STICKY
  );

  React.useLayoutEffect(() => {
    setTimeout(() => {
      const pinWrapper = document.getElementById("pin-comments");
      if (pinWrapper && ref.current && !isMobile) {
        ref.current.style.paddingBottom = `${pinWrapper?.clientHeight || 0}px`;
      }
      if (refStickyInner.current && isMobile) {
        setHasPinExpand(refStickyInner.current.clientHeight > 15);
      }
    }, 1000);
  }, [pinComment, isMobile]);

  React.useEffect(() => {
    // Hide comment after 5 seconds on mobile
    setHideComment(false);
    if (isOverlay) {
      setHideComment(false);
      if (timeoutRef?.current) {
        window.clearTimeout(timeoutRef?.current);
      }
      if (timeoutRef) {
        timeoutRef.current = window.setTimeout(() => {
          setHideComment(true);
        }, 5000);
      }
    }
  }, [comments]);

  React.useEffect(() => {
    if (
      state.triggerScrollBottom &&
      !delayScroll.current &&
      commentList.length &&
      refChatList.current
    ) {
      delayScroll.current = true;
      setTimeout(() => {
        delayScroll.current = false;
        refChatList?.current?.scrollTo({
          top:
            refChatList?.current?.scrollHeight -
            refChatList?.current?.clientHeight,
          behavior: "auto",
        });
        setState({
          ...state,
          triggerScrollBottom: true,
        });
      }, 200);
    }
  }, [commentList]);

  function handleCreateComment() {
    if (refInputComment.current?.value && createComment) {
      const isRehearsal =
        statusLive !== StatusLive.before &&
        program.livestreamStatus === LiveStreamStatus.BEFORE
          ? 1
          : 0;
      createComment(refInputComment.current.value, isRehearsal);
      refInputComment.current.value = "";
      setDisableSubmit(true);
    }
  }

  function handleScroll() {
    if (
      refChatList.current?.clientHeight &&
      refChatList.current?.scrollTop &&
      refChatList.current?.scrollHeight
    ) {
      setState({
        ...state,
        triggerScrollBottom:
          refChatList.current?.clientHeight + refChatList.current?.scrollTop ===
          refChatList.current?.scrollHeight,
      });
    }
  }

  function setNameChatModal() {
    if (!LocalStorage.get(LocalStorageKey.userName)) {
      confirm({
        className: styles.modalConfirm,
        width: 490,
        title: "名前を入れてコメントしよう",
        icon: "",
        centered: true,
        okText: "決定",
        cancelText: "閉じる",
        okButtonProps: {
          className: styles.button,
        },
        content: (
          <Input
            ref={refInputConfirm}
            className={styles.inputConfirm}
            placeholder="ニックネーム"
          />
        ),
        onOk() {
          if (refInputConfirm?.current?.state?.value) {
            LocalStorage.set(
              LocalStorageKey.userName,
              refInputConfirm?.current?.state?.value
            );
            setUserName(refInputConfirm?.current?.state?.value);
            updateCartInUserName();
          }
        },
        onCancel() {},
      });
    }
  }

  function editNameChatModal() {
    if (userName) {
      confirm({
        className: styles.modalConfirm,
        width: 490,
        title: "ニックネームを変更する",
        icon: "",
        centered: true,
        okText: "決定",
        cancelText: "閉じる",
        okButtonProps: {
          className: styles.button,
        },
        content: (
          <Input
            ref={refInputConfirm}
            className={styles.inputConfirm}
            defaultValue={userName}
          />
        ),
        onOk() {
          if (refInputConfirm?.current?.state?.value) {
            LocalStorage.remove(LocalStorageKey.userName);
            LocalStorage.set(
              LocalStorageKey.userName,
              refInputConfirm?.current?.state?.value
            );
            setUserName(refInputConfirm?.current?.state?.value);
            updateCartInUserName();
          }
        },
        onCancel() {},
      });
    }
  }

  function handleClickStickyComment() {
    if (isMobile && hasPinExpand) {
      setPinExpand(!pinExpand);
    }
  }

  const renderSendIcon = () => {
    return (
      <>
        <img
          className={`${styles.sendIconChat} ${
            !userName || disableComment ? styles.sendIconChatDisable : ""
          }`}
          src={isMobile ? icSendSp : icSendPc}
          alt="ic_send"
        />
        <img
          className={`${styles.sendIconChatDark} ${
            !userName || disableComment ? styles.sendIconChatDisable : ""
          }`}
          src={isMobile ? icSendSpDark : icSendPcDark}
          alt="ic_send"
        />
      </>
    );
  };

  const isRemoveChatContent =
    isMobile && isOverlay && statusLive === StatusLive.after;

  if (!isFetched) {
    return null;
  }

  return (
    <div
      className={`${styles.commentContainer} ${
        isOverlay ? styles.commentOverlay : ""
      }
      ${isRemoveChatContent ? styles.overlayResize : ""}`}
    >
      {statusLive !== StatusLive.before && (
        <div className={styles.selectAll}>
          <Select
            dropdownMatchSelectWidth={false}
            defaultValue={state.filter}
            className={styles.select}
            onChange={(value: string) => {
              setState({ ...state, filter: value });
            }}
          >
            <Option value="all">すべて</Option>
            <Option value="manager">
              {get(program, "customer.items[0].companyName", "")}
            </Option>
          </Select>
        </div>
      )}
      <div
        className={`${styles.contentChat} ${
          hideComment ? styles.hideComment : ""
        }`}
        ref={ref}
      >
        {statusLive !== StatusLive.before ? (
          <>
            {!isRemoveChatContent && (
              <div
                className={styles.contentChatList}
                ref={refChatList}
                onScroll={debounce(handleScroll, 100)}
              >
                {commentList.map((comment) => (
                  <div className={styles.commentItem} key={comment.id}>
                    <div className={styles.rowTop}>
                      <div className={styles.nameChat}>
                        {comment.userId === "manager" ? (
                          <>
                            {get(program.customer, "items[0].companyName", "")}
                            <CheckCircleFilled
                              className={styles.checkCircleIcon}
                            />
                          </>
                        ) : (
                          comment.userName
                        )}
                      </div>
                      <div className={styles.time}>
                        <FromNow date={comment.commentDateTime} />
                      </div>
                    </div>
                    <div className={styles.commentDetail}>
                      {comment.comment}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(!isMobile || (isOverlay && pinComment.length > 0)) &&
            statusLive !== StatusLive.after &&
            !program.uploadVideoUrl ? (
              <TransitionGroup
                className={styles.stickyComments}
                id="pin-comments"
              >
                {pinComment.map((pin: UpdateCommentInput) => (
                  <CSSTransition key={pin.dataType} timeout={500}>
                    <div
                      className="sticky-comment"
                      onClick={debounce(handleClickStickyComment, 1000)}
                    >
                      <h5 className={styles.stickyHeading}>
                        {pin.userId === "manager" ? (
                          <>
                            {get(program.customer, "items[0].companyName", "")}
                            <CheckCircleFilled
                              className={styles.checkCircleIcon}
                            />
                          </>
                        ) : (
                          pin.userName
                        )}
                      </h5>
                      <div
                        className={`${styles.stickyContent} ${
                          hasPinExpand ? styles.hasPinExpand : ""
                        } ${pinExpand ? styles.expand : ""}`}
                      >
                        <div
                          className={styles.stickyContentInner}
                          ref={refStickyInner}
                        >
                          {pin.comment}
                        </div>
                      </div>
                    </div>
                  </CSSTransition>
                ))}
              </TransitionGroup>
            ) : null}
          </>
        ) : (
          <div className={styles.noLiveText}>
            配信前はコメントできません。
            <br />
            ライブ配信が開始されましたら <br />
            みなさまのコメントお待ちしております。
          </div>
        )}
      </div>
      {statusLive !== StatusLive.before && (
        <Form className={styles.boxChat} onFinish={handleCreateComment}>
          {userName ? (
            <h5 className={styles.commentHeading}>
              {userName}
              <Button
                type="text"
                className={styles.edit}
                onClick={() => editNameChatModal()}
              >
                <EditOutlined />
              </Button>
            </h5>
          ) : (
            <h5 className={styles.commentHeading}>No Name</h5>
          )}
          <div className={styles.chat}>
            <span className={styles.messageIcon} />
            {!userName ? (
              <span
                className={styles.inputChatPlaceholder}
                onClick={() => setNameChatModal()}
              >
                コメントを送ろう
              </span>
            ) : (
              <input
                type="text"
                ref={refInputComment}
                className={styles.inputChat}
                placeholder="コメントを送ろう"
                // eslint-disable-next-line
                onChange={(e: any) => {
                  if (e.target.value) {
                    setDisableSubmit(false);
                  }
                }}
              />
            )}
          </div>
          <Button
            type="text"
            htmlType="submit"
            className={styles.sendIcon}
            icon={renderSendIcon()}
            disabled={!userName || disableComment}
          />
        </Form>
      )}
    </div>
  );
};

export default LiveStreamingChat;
